'use client';

import { useState } from 'react';
import cn from 'classnames';
import { FormikErrors } from 'formik';
import BaseModal from '@/components/modal/lib/BaseModal';
import PriveeKeyholeMaroon from 'public/img/icon/keyhole-red.png';
import Image from 'next/image';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';
import Form from '@/components/form/lib/Form/Form';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import Link from 'next/link';
import { isValidEmail, isValidBirthday } from '@/util/regexp';
import { phoneMaskPipe } from '@/util/phone';
import { useModal } from '@/components/hook/modal';
import InterestedInPriveeThankYouModal from '@/app/reserve/ExpressedInterestThankYouModal/PriveeExpressedInterestThankYouModal';
import { doesEmailExist } from '@/util/user_client';

import './PriveeLoggedOutExpressInterestModal.style.scss';
import CheckBox from '@/components/common/CheckBox/CheckBox';
import analytics from '@/util/analytics';
import { SMS_OPTIN_COPY } from '@/const/sms_optin_copy';

type PriveeLoggedOutExpressInterestModalProps = {
  onClose: () => void;
};

export type PriveeLoggedOutExpressInterestFormValues = {
  name: string;
  email: string;
  phone: string;
  birthday: string;
};

const initialValues: PriveeLoggedOutExpressInterestFormValues = {
  name: '',
  email: '',
  phone: '',
  birthday: ''
};

function validate(
  values: PriveeLoggedOutExpressInterestFormValues
): FormikErrors<PriveeLoggedOutExpressInterestFormValues> {
  const errors: FormikErrors<PriveeLoggedOutExpressInterestFormValues> = {};

  if (!values.name.trim()) {
    errors.name = 'Please provide your name';
  }

  if (values.name.trim().split(' ').length < 2) {
    errors.name = 'Please provide your first and last name';
  }

  if (!values.email) {
    errors.email = 'Please provide your email';
  } else if (!isValidEmail(values.email)) {
    errors.email = 'Please provide a valid email address';
  }

  if (!values.phone) {
    errors.phone = 'Please provide your phone number';
  } else if (values.phone.length < 14) {
    /* Phone totals 14 with masked characters */
    errors.phone = 'Please enter a valid 10-digit phone number';
  }

  if (!values.birthday || values.birthday.length < 10) {
    errors.birthday = 'Please provide your birthday';
  } else if (!isValidBirthday(values.birthday)) {
    errors.birthday = 'Please enter a valid birth date';
  }

  return errors;
}

function PriveeLoggedOutExpressInterestModal({
  onClose
}: PriveeLoggedOutExpressInterestModalProps) {
  const { showModal } = useModal();
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [emailOfExistingUser, setEmailOfExistingUser] = useState<string | null>(
    null
  );
  const [smsOptIn, setSmsOptIn] = useState(false);

  const handleEmailBlur = async (event: React.FocusEvent<HTMLDivElement>) => {
    const emailValue = (event.target as HTMLInputElement).value;
    if (!isValidEmail(emailValue)) return;
    const { userExists } = await doesEmailExist(emailValue);
    setEmailOfExistingUser(userExists ? emailValue : null);
  };

  const handleSubmit = async (
    values: PriveeLoggedOutExpressInterestFormValues
  ) => {
    setSubmissionStatus('submitting');

    const { userExists } = await doesEmailExist(values.email);
    if (userExists) {
      setEmailOfExistingUser(values.email);
      setSubmissionStatus('idle');
      return;
    }

    try {
      const nameParts = values.name.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName =
        nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;

      const response = await fetch('/api/privee/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          first_name: firstName,
          last_name: lastName,
          phone: values.phone || undefined,
          birthday: values.birthday
            ? values.birthday.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$1-$2')
            : undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      if (smsOptIn) {
        try {
          const leadCaptureResponse = await fetch(
            '/api/marketing/leadCapture',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: values.email,
                phone: values.phone,
                source: 'privee_logged-out_express-interest-button',
                optInText: true,
                attributes: {
                  firstName,
                  lastName,
                  interested_in_privee: true
                }
              })
            }
          );

          if (!leadCaptureResponse.ok) {
            const errorBody = await leadCaptureResponse.text();
            console.warn('Braze opt-in failed body:', errorBody);
            throw new Error(
              `Failed to opt-in user ${values.email} for SMS: ${leadCaptureResponse.status} ${leadCaptureResponse.statusText}`
            );
          }
        } catch (error) {
          // Just log if Braze fails; do not block flow
          console.warn(error);
        }
      }

      showModal(<InterestedInPriveeThankYouModal onClose={onClose} />);
    } catch {
      setSubmissionStatus('error');
    }
  };

  return (
    <BaseModal
      className='privee-logged-out-interest-modal'
      onClose={onClose}
    >
      <div className='keyhole-img-container'>
        <Image
          src={PriveeKeyholeMaroon}
          alt='Maroon keyhole silhouette'
          width={365}
          height={739}
        />
      </div>
      <Text
        className='privee-logged-out-interest-title'
        variant='title-xl'
        italic
      >
        Unlock R&eacute;serv&eacute;
      </Text>
      <Text
        className='privee-logged-out-interest-description'
        variant='text-m'
      >
        <span className='privee-logged-out-interest-description--bold'>
          R&eacute;serv&eacute; is available exclusively to active Vivrelle
          members.
        </span>{' '}
        Not a member yet? You can still express interest and be considered for a
        future invitation once&nbsp;you&nbsp;join.
      </Text>
      <Form<PriveeLoggedOutExpressInterestFormValues>
        className='privee-logged-out-interest-form'
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        validateOnBlur
      >
        <InputField
          name='name'
          label='Name'
          data-testid='privee-interest-name'
        />
        <div
          className={cn({ 'email-exists-error': emailOfExistingUser })}
          onInput={() => emailOfExistingUser && setEmailOfExistingUser(null)}
          onBlur={handleEmailBlur}
        >
          <InputField
            name='email'
            label='Email'
            type='email'
            data-testid='privee-interest-email'
          />
          {emailOfExistingUser && (
            <div className='InputField-error'>
              <Text className='InputField-error-text'>
                This email is already registered.{' '}
                <Link
                  href={`/login?email=${encodeURIComponent(
                    emailOfExistingUser
                  )}`}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  Login here.
                </Link>
              </Text>
            </div>
          )}
        </div>
        <MaskedField
          guide={false}
          mask={[
            '(',
            /[1-9]/,
            /\d/,
            /\d/,
            ')',
            ' ',
            /\d/,
            /\d/,
            /\d/,
            '-',
            /\d/,
            /\d/,
            /\d/,
            /\d/
          ]}
          pipe={phoneMaskPipe}
          label='Phone Number'
          name='phone'
          type='tel'
          autoComplete='tel'
          placeholder='(xxx) xxx-xxxx'
          data-testid='privee-interest-phone'
        />
        <MaskedField
          guide={false}
          mask={[
            /[0-1]/,
            /[0-9]/,
            '/',
            /[0-3]/,
            /[0-9]/,
            '/',
            /[1-2]/,
            /\d/,
            /\d/,
            /\d/
          ]}
          label='Birthday'
          name='birthday'
          placeholder='mm/dd/yyyy'
          data-testid='privee-interest-birthday'
        />
        <CheckBox
          className='sms-opt-in'
          checked={smsOptIn}
          onChange={() => {
            const nextValue = !smsOptIn;
            setSmsOptIn(nextValue);
            analytics.track(
              `Privee Express Interest modal (logged out) - SMS Opt-in Clicked`,
              {
                optIn: nextValue
              }
            );
          }}
          label={
            <span className='sms-opt-in-text'>
              {SMS_OPTIN_COPY}{' '}
              <a
                href='/terms'
                className='terms-link'
                target='_blank'
                rel='noopener noreferrer'
              >
                Terms & Conditions.
              </a>
            </span>
          }
        />
        <Button
          className={cn('privee-logged-out-interest-button', {
            'is-submitting': submissionStatus === 'submitting'
          })}
          variant='dark'
          type='submit'
          disabled={submissionStatus === 'submitting'}
        >
          {submissionStatus === 'submitting'
            ? 'Submitting...'
            : 'Submit Interest'}
        </Button>

        {submissionStatus === 'error' && (
          <Text
            className='privee-logged-out-interest-error-text'
            variant='text-s'
          >
            Sorry, something went wrong. Please refresh and try&nbsp;again.
          </Text>
        )}
      </Form>
      <Link
        className='privee-logged-out-interest-privacy-link'
        href='/privacy-policy'
        target='_blank'
      >
        View our Privacy Policy
      </Link>
    </BaseModal>
  );
}

export default PriveeLoggedOutExpressInterestModal;
